/**
 * 인증 React Query 훅 모음.
 *
 * 엔드포인트 계약 (src/jeeum/api/auth.py):
 *   POST /auth/login    { email, password } → { ok: true, name } + Set-Cookie jeeum_auth
 *   POST /auth/register { email, password, name, invite_code } → { ok: true } + Set-Cookie jeeum_auth
 *   POST /auth/logout                       → { ok: true }        + Delete-Cookie jeeum_auth
 *   POST /auth/impersonation/consume        → { ok: true }        + target session cookies
 *   POST /auth/impersonation/stop           → { ok: true }        + restored admin session
 *   GET  /auth/me                           → AuthUser            (401 시 throw)
 *
 * 쿠키는 httpJson 이 `credentials: 'include'` 로 자동 전송. Vite dev 프록시가
 * `/auth` 를 :8000 으로 전달하므로 동일 오리진처럼 동작.
 */

import { useCallback, useState } from 'react';

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryFunctionContext,
  type UseQueryOptions,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { httpJson } from '../../lib/http';
import type { HttpError } from '../../lib/http';
import { clearAuthCookieHint, setAuthCookieHint } from './cookies';
import { useAuthStore, type AuthUser } from './store';

export const AUTH_ME_KEY = ['auth', 'me'] as const;
export const AUTH_ME_REQUEST_TIMEOUT_MS = 5_000;

export interface LoginInput {
  email: string;
  password: string;
}

export interface LoginResponse {
  ok: boolean;
  name?: string;
}

export interface RegisterInput {
  email: string;
  password: string;
  name: string;
  invite_code: string;
}

export interface RegisterResponse {
  ok: boolean;
}

export interface LogoutResponse {
  ok: boolean;
}

export interface ImpersonationResponse {
  ok: boolean;
}

export class ImpersonationSessionSyncError extends Error {
  override readonly cause: unknown;

  constructor(cause: unknown) {
    super('The session changed, but the current user could not be synchronized.');
    this.name = 'ImpersonationSessionSyncError';
    this.cause = cause;
  }
}

export interface ConsumeImpersonationController {
  isPending: boolean;
  mutateAsync: (token: string) => Promise<ImpersonationResponse>;
}

export interface StopImpersonationController {
  isPending: boolean;
  mutateAsync: () => Promise<ImpersonationResponse>;
}

interface MeResponse {
  user: AuthUser | null;
}

function createTimedRequestSignal(
  parentSignal: AbortSignal,
  timeoutMs: number,
) {
  const controller = new AbortController();
  const abortFromParent = () => controller.abort(parentSignal.reason);
  if (parentSignal.aborted) {
    abortFromParent();
  } else {
    parentSignal.addEventListener('abort', abortFromParent, { once: true });
  }
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  return {
    signal: controller.signal,
    dispose: () => {
      clearTimeout(timeout);
      parentSignal.removeEventListener('abort', abortFromParent);
    },
  };
}

async function fetchMe({
  signal,
}: QueryFunctionContext): Promise<AuthUser | null> {
  const request = createTimedRequestSignal(signal, AUTH_ME_REQUEST_TIMEOUT_MS);
  try {
    const res = await httpJson.get<MeResponse>('/auth/me', {
      signal: request.signal,
    });
    if (res.user) {
      setAuthCookieHint();
      return res.user;
    }
    clearAuthCookieHint();
    return null;
  } finally {
    request.dispose();
  }
}

export function consumeImpersonationToken(
  token: string,
): Promise<ImpersonationResponse> {
  return httpJson.post<ImpersonationResponse>('/auth/impersonation/consume', {
    token,
  });
}

export function stopImpersonation(): Promise<ImpersonationResponse> {
  return httpJson.post<ImpersonationResponse>('/auth/impersonation/stop');
}

async function syncAuthUser(
  queryClient: ReturnType<typeof useQueryClient>,
  setUser: (user: AuthUser | null) => void,
) {
  await queryClient.invalidateQueries({ queryKey: AUTH_ME_KEY });
  const fresh = await queryClient.fetchQuery<AuthUser | null>({
    queryKey: AUTH_ME_KEY,
    queryFn: fetchMe,
    retry: false,
    staleTime: 0,
  });
  setUser(fresh);
  // 로그인 전 보호 API에서 받은 401/실패 결과가 캐시에 남아 있으면 로그인 직후
  // 스튜디오가 정상 응답을 받아도 오류 화면이 고착될 수 있다.
  queryClient.removeQueries({ queryKey: ['separation'] });
}

async function syncAfterIdentitySwitch(
  queryClient: ReturnType<typeof useQueryClient>,
  resetAuthState: () => void,
  setUser: (user: AuthUser | null) => void,
) {
  try {
    await queryClient.cancelQueries();
    queryClient.clear();
    resetAuthState();

    const fresh = await queryClient.fetchQuery<AuthUser | null>({
      queryKey: AUTH_ME_KEY,
      queryFn: fetchMe,
      retry: false,
      staleTime: 0,
    });
    if (!fresh) {
      throw new Error('The switched session did not return an authenticated user.');
    }
    setUser(fresh);
  } catch (error) {
    throw new ImpersonationSessionSyncError(error);
  }
}

async function waitForPendingMutations(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  // MutationCache.clear()는 이미 실행 중인 callback을 취소하지 못한다. 기존 계정의
  // mutation이 완전히 끝난 뒤에만 서버 cookie 전환 요청을 시작한다.
  while (queryClient.isMutating() > 0) {
    await new Promise<void>((resolve) => {
      let unsubscribe: (() => void) | undefined;
      const resolveWhenIdle = () => {
        if (queryClient.isMutating() !== 0) return;
        unsubscribe?.();
        resolve();
      };
      unsubscribe = queryClient.getMutationCache().subscribe(resolveWhenIdle);
      resolveWhenIdle();
    });
  }
}

/** `/auth/me` 쿼리. 응답의 `{user}` 래퍼를 벗겨 `AuthUser | null` 로 정규화. */
export function useMeQuery(
  options?: Omit<
    UseQueryOptions<AuthUser | null, HttpError>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<AuthUser | null, HttpError> {
  return useQuery<AuthUser | null, HttpError>({
    queryKey: AUTH_ME_KEY,
    queryFn: fetchMe,
    // me 쿼리는 401 을 정상 경로로 취급 — 재시도 금지
    retry: false,
    // 로그인 상태는 자주 변하지 않는다 (JWT 만료는 새 요청 때 자연스레 처리)
    staleTime: 60_000,
    ...options,
  });
}

export function useLoginMutation(): UseMutationResult<
  LoginResponse,
  HttpError,
  LoginInput
> {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);

  return useMutation<LoginResponse, HttpError, LoginInput>({
    mutationFn: (input) =>
      httpJson.post<LoginResponse>('/auth/login', input),
    onSuccess: async () => {
      // auth 응답 본문에는 full user 가 없으므로 me 를 재요청해 캐시 갱신
      await syncAuthUser(queryClient, setUser);
    },
  });
}

export function useRegisterMutation(): UseMutationResult<
  RegisterResponse,
  HttpError,
  RegisterInput
> {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);

  return useMutation<RegisterResponse, HttpError, RegisterInput>({
    mutationFn: (input) =>
      httpJson.post<RegisterResponse>('/auth/register', input),
    onSuccess: async () => {
      await syncAuthUser(queryClient, setUser);
    },
  });
}

export function useLogoutMutation(): UseMutationResult<
  LogoutResponse,
  HttpError,
  void
> {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);

  return useMutation<LogoutResponse, HttpError, void>({
    mutationFn: () => httpJson.post<LogoutResponse>('/auth/logout'),
    onSuccess: async () => {
      clearAuthCookieHint();
      setUser(null);
      queryClient.removeQueries({ queryKey: AUTH_ME_KEY });
    },
  });
}

export function useConsumeImpersonationMutation(): ConsumeImpersonationController {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  const resetAuthState = useAuthStore((s) => s.reset);
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = useCallback(
    async (token: string) => {
      setIsPending(true);
      try {
        await waitForPendingMutations(queryClient);
        const response = await consumeImpersonationToken(token);
        await syncAfterIdentitySwitch(queryClient, resetAuthState, setUser);
        return response;
      } finally {
        setIsPending(false);
      }
    },
    [queryClient, resetAuthState, setUser],
  );

  return { isPending, mutateAsync };
}

export function useStopImpersonationMutation(): StopImpersonationController {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  const resetAuthState = useAuthStore((s) => s.reset);
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = useCallback(async () => {
    setIsPending(true);
    try {
      await waitForPendingMutations(queryClient);
      const response = await stopImpersonation();
      await syncAfterIdentitySwitch(queryClient, resetAuthState, setUser);
      return response;
    } finally {
      setIsPending(false);
    }
  }, [queryClient, resetAuthState, setUser]);

  return { isPending, mutateAsync };
}
